import React, { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import logoUrl from '../../assets/logo.png';
import Sidebar from './Sidebar';
import { ToastContext } from '../../contexts/ToastContext';
import { MailFoldersContext } from '../../contexts/MailFoldersContext';
import ToastContainer from '../ToastContainer';
import { getMailFolders } from '../../api/mailbox';
import type { Toast, ToastVariant } from '../../contexts/ToastContext';
import type { MailFolder } from '../../api/mailbox';

let _toastIdSeq = 0;
function nextId() { return String(++_toastIdSeq); }

/**
 * AppShell wraps all authenticated application pages.
 *
 * Provides:
 *  - Fixed sidebar + main content slot
 *  - ToastContext (global toast queue)
 *  - MailFoldersContext (folder list, fetched once per session)
 *  - Mobile hamburger toggle
 */
export default function AppShell() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);

  // Fetch mail folders once on mount (used by rules editor)
  useEffect(() => {
    getMailFolders()
      .then((res) => setFolders(res.folders))
      .catch(() => { /* folders unavailable — graceful degradation */ })
      .finally(() => setFoldersLoading(false));
  }, []);

  function addToast(variant: ToastVariant, message: string) {
    const id = nextId();
    setToasts((prev) => [...prev, { id, variant, message }]);
    // Auto-dismiss after 5 s
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }

  function removeToast(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      <MailFoldersContext.Provider value={{ folders, loading: foldersLoading }}>
        <div className="d-flex" style={{ minHeight: '100vh' }}>
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="d-md-none position-fixed top-0 start-0 w-100 h-100"
              style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1040 }}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar (desktop always visible; mobile slide-in) */}
          <div
            className={`d-md-flex flex-column position-fixed top-0 start-0 h-100${sidebarOpen ? ' d-flex' : ' d-none'}`}
            style={{ zIndex: 1045 }}
          >
            <Sidebar />
          </div>

          {/* Main content area (offset for sidebar on desktop) */}
          <div className="flex-grow-1" style={{ marginLeft: 0 }}>
            {/* Mobile topbar */}
            <div className="d-md-none d-flex align-items-center px-3 py-2 bg-white border-bottom">
              <button
                className="btn btn-sm btn-outline-secondary me-3"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <i className="bi bi-list fs-5" aria-hidden="true" />
              </button>
              <img src={logoUrl} alt="Catty" width={737} height={759} style={{ height: 28, width: 'auto' }} />
            </div>

            {/* Desktop sidebar offset */}
            <div className="d-none d-md-block" style={{ marginLeft: '220px' }}>
              <main className="p-4">
                <Outlet />
              </main>
              <footer className="text-center py-3 border-top" style={{ marginTop: 'auto', fontSize: '0.75rem', color: '#94a3b8' }}>
                &copy; {new Date().getFullYear()} Joined-up Data Ltd
                {' · '}
                <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms</Link>
                {' · '}
                <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>Privacy</Link>
              </footer>
            </div>

            {/* Mobile main content */}
            <div className="d-block d-md-none">
              <main className="p-3">
                <Outlet />
              </main>
              <footer className="text-center py-3 border-top" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                &copy; {new Date().getFullYear()} Joined-up Data Ltd
                {' · '}
                <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms</Link>
                {' · '}
                <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>Privacy</Link>
              </footer>
            </div>
          </div>
        </div>

        <ToastContainer />
      </MailFoldersContext.Provider>
    </ToastContext.Provider>
  );
}
